import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { omitForbiddenUpdateFields } from '~/modules/resourcing/common/util/resourceAllocationUtil';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  updateAllocationSeriesData,
  UPDATE_RESOURCE_ALLOCATION
} from '~/modules/resourcing/hooks/useUpdateResourceAllocation';
import { RESOURCE_ALLOCATIONS_QUERY_BY_USER } from './useResourceAllocationsSummary';
import useQuickAllocationContext from './useQuickAllocationContext';
import {
  getAllocatedHoursByDateRange,
  tryLoadCachedAllocationSummaryQuery,
  updateResourceUsersSummaryAllocationBar
} from './useFulfilledResourceAllocationHandler';
import { mapProjectStatusToProjectWorkflowStages } from './utils';

export const updateCache = ({
  resourceUsersCurrentPage,
  dateRange,
  userSort,
  allocationSort,
  periodResolution,
  scale,
  me,
  existingResourceAllocation,
  filter,
  projectStatusFilterOption
}) => (proxy, mutationResponse) => {
  const {
    data: {
      updateResourceAllocation2: { resourceAllocation }
    }
  } = mutationResponse;

  updateResourceUsersSummaryAllocationBar({
    resourceUsersCurrentPage,
    proxy,
    resourceAllocation,
    sort: userSort,
    periodResolution,
    dateRange,
    existingResourceAllocation,
    filter,
    updateHoursCallback: ({ periodHours, existingPeriodHours = 0, summary }) =>
      periodHours > 0
        ? periodHours - existingPeriodHours + summary.allocatedDuration.hours
        : existingPeriodHours > 0
        ? summary.allocatedDuration.hours - existingPeriodHours
        : summary.allocatedDuration.hours
  });

  const allocationVariables = {
    page: 1,
    dateRange,
    userUri: resourceAllocation.user.userUri,
    periodResolution,
    limit: 5,
    sort: allocationSort,
    projectWorkflowStages: mapProjectStatusToProjectWorkflowStages(
      projectStatusFilterOption
    )
  };

  const results = tryLoadCachedAllocationSummaryQuery({
    proxy,
    variables: allocationVariables
  });

  if (!results) return;

  const { resourceAllocationsSummary } = results;

  proxy.writeQuery({
    query: RESOURCE_ALLOCATIONS_QUERY_BY_USER,
    variables: allocationVariables,
    data: {
      resourceAllocationsSummary: getUpdatedResourceAllocationSummary({
        me,
        scale,
        resourceAllocation,
        resourceAllocationsSummary
      })
    }
  });

  if (existingResourceAllocation && existingResourceAllocation.periodDetails) {
    updateAllocationSeriesData({
      proxy,
      newAllocation: resourceAllocation,
      originalAllocation: existingResourceAllocation
    });
  }
};

const getUpdatedResourceAllocationSummary = ({
  resourceAllocationsSummary,
  resourceAllocation,
  me,
  scale
}) => {
  const resourceAllocatedHoursByDateRange = getAllocatedHoursByDateRange({
    me,
    resourceAllocation,
    scale
  });

  return {
    ...resourceAllocationsSummary,
    resourceAllocationSummaryByDateRange: resourceAllocationsSummary.resourceAllocationSummaryByDateRange.map(
      sr =>
        sr.resourceAllocation.id === resourceAllocation.id
          ? {
              ...sr,
              resourceAllocatedHoursByDateRange,
              resourceAllocation: {
                ...resourceAllocation,
                project: sr.resourceAllocation.project,
                role: sr.resourceAllocation.role
              }
            }
          : sr
    )
  };
};

const useResourceAllocationUpdateHandler = ({ allocationSort }) => {
  const me = useMeContext();
  const {
    scale,
    resourceUsersCurrentPage,
    sort: userSort,
    dateRange,
    periodResolution,
    filter,
    projectStatusFilterOption
  } = useQuickAllocationContext();

  const [updateResourceAllocation, { data, loading }] = useMutation(
    UPDATE_RESOURCE_ALLOCATION
  );

  const onUpdateResourceAllocation = useCallback(
    ({ allocation, existingResourceAllocation }) =>
      updateResourceAllocation({
        variables: {
          input: omitForbiddenUpdateFields(allocation)
        },
        update: updateCache({
          resourceUsersCurrentPage,
          dateRange,
          userSort: [userSort],
          allocationSort,
          periodResolution,
          scale,
          me,
          existingResourceAllocation,
          filter,
          projectStatusFilterOption
        })
      }),
    [
      resourceUsersCurrentPage,
      updateResourceAllocation,
      dateRange,
      userSort,
      allocationSort,
      periodResolution,
      scale,
      me,
      filter
    ]
  );

  return {
    onUpdateResourceAllocation,
    loading,
    data
  };
};

export default useResourceAllocationUpdateHandler;
